import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { PERSONAL_INFORMATION_API } from "../api";
import {
  setIsNewForPersonalInformation,
  setIsDataChangedForPersonal,
  addErrorMessage,
  addSuccessMessage,
  setPersonalInformationData,
} from "../slice";
import { useDate } from "./";

export const usePersonalInfo = ({ hasEditAccess }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { simplePersianToGregorian, simpleGregorianToPersian } = useDate();
  const { data, isNew, isDataChanged } = useSelector((state) => state.PersonalInformation);

  const { mutateAsync: addPersonalInformation } = useMutation((body) =>
    PERSONAL_INFORMATION_API.AddPersonalInformation(body)
  );

  const { mutateAsync: editPersonalInformation } = useMutation((body) =>
    PERSONAL_INFORMATION_API.EditPersonalInformation(body)
  );

  const { mutate: getPersonalInformation } = useMutation(
    () => PERSONAL_INFORMATION_API.GetPersonalInformation(),
    {
      onSuccess: ({ data: personalInformation }) => {
        let children = [];
        let separationDate = "";
        if (personalInformation.success && personalInformation.data) {
          if (personalInformation.data.separationDate) {
            separationDate = simpleGregorianToPersian(
              personalInformation.data.separationDate.split("T")[0]
            );
          }
          if (personalInformation.data.children) {
            children = personalInformation.data.children.map((child) => {
              if (child.birthDate) {
                return {
                  ...child,
                  birthDate: simpleGregorianToPersian(child.birthDate.split("T")[0]),
                };
              }
              return child;
            });
          }
          dispatch(
            setPersonalInformationData({
              ...personalInformation.data,
              separationDate,
              militaryServiceStatus:
                personalInformation.data.militaryServiceStatus === 0
                  ? 5
                  : personalInformation.data.militaryServiceStatus,
              children,
            })
          );
        }
      },
      staleTime: Infinity,
    }
  );

  const handleSubmit = async (route) => {
    if (!isDataChanged) {
      navigate(route);
      return;
    }
    let separationDate = undefined;
    if (data.separationDate) {
      separationDate = simplePersianToGregorian(data.separationDate);
    }
    let children = data.children.map((child) => {
      if (child.birthDate) {
        return {
          ...child,
          birthDate: simplePersianToGregorian(child.birthDate),
        };
      }
      let newChild = { ...child };
      delete newChild.birthDate;
      return newChild;
    });
    let body = { ...data, separationDate, children };
    if (hasEditAccess) {
      if (isNew) {
        await addPersonalInformation(body, {
          onSuccess: ({ data }) => {
            if (data.success) {
              dispatch(setIsNewForPersonalInformation(false));
              dispatch(addSuccessMessage(data?.message));
              navigate(route);
            }
          },
          onSettled: () => {
            dispatch(setIsDataChangedForPersonal(false));
          },
          onError: ({ response }) => {
            dispatch(addErrorMessage(response));
          },
        });
      } else {
        await editPersonalInformation(body, {
          onSuccess: ({ data }) => {
            if (data.success) {
              dispatch(setIsNewForPersonalInformation(false));
              dispatch(addSuccessMessage(data?.message));
              navigate(route);
            }
          },
          onSettled: () => {
            dispatch(setIsDataChangedForPersonal(false));
          },
          onError: ({ response }) => {
            dispatch(addErrorMessage(response));
          },
        });
      }
    } else {
      navigate(route);
    }
  };
  return { handleSubmit, getPersonalInformation };
};
